import React, { useEffect, useMemo, useRef, useState } from "react";
import "./SuperadminEditor.css";
import {
  AgentPayload,
  AgentRecord,
  deleteAgent,
  fetchAgents,
  saveAgent,
} from "../../utils/api";
import Slider from "../../components/Slider";
import Menu from "../../components/Menu";
import Modal from "../../components/Modal";
import JsonEditor from "../../components/Superadmin/JsonEditor";

const SUPERADMIN_CLIPBOARD_KEY = "operatorfe-superadmin-clipboard";
const SUPERADMIN_CLIPBOARD_PREFIX = "OPERATORFE_SUPERADMIN_CLIPBOARD=";

type SelectedAgentId = string | "new" | null;

const DEFAULT_PAGE_PREFIX = "/lot/{lotId}/";

type AgentFormState = {
  agentKey: string;
  name: string;
  description: string;
  prompt: string;
  enabled: boolean;
  payloadTemplateText: string;
  allowedFieldsText: string;
  lockedFieldsText: string;
  prefetchEndpoint: string;
  actionEndpoint: string;
  actionMethod: string;
  confirmLabel: string;
  successMarkdown: string;
  tags: string[];
  pageMode: "prefixed" | "custom";
  pageSuffix: string;
  pageCustom: string;
};

const emptyAgent: AgentFormState = {
  agentKey: "",
  name: "",
  description: "",
  prompt: "",
  enabled: true,
  payloadTemplateText: "",
  allowedFieldsText: "",
  lockedFieldsText: "",
  prefetchEndpoint: "",
  actionEndpoint: "",
  actionMethod: "POST",
  confirmLabel: "Approve",
  successMarkdown: "Agent action completed.",
  tags: ["Agent", "Task"],
  pageMode: "prefixed",
  pageSuffix: "",
  pageCustom: "",
};

const formatJsonText = (value?: any) => {
  if (!value || (typeof value === "object" && Object.keys(value).length === 0)) {
    return "";
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
};

const buildPageState = (page?: string | null) => {
  const trimmed = page?.trim() ?? "";
  if (!trimmed) {
    return {
      pageMode: "prefixed" as const,
      pageSuffix: "",
      pageCustom: "",
    };
  }
  if (trimmed.startsWith(DEFAULT_PAGE_PREFIX)) {
    const suffix = trimmed.slice(DEFAULT_PAGE_PREFIX.length).replace(/^\/+/g, "");
    return {
      pageMode: "prefixed" as const,
      pageSuffix: suffix,
      pageCustom: "",
    };
  }
  return {
    pageMode: "custom" as const,
    pageSuffix: "",
    pageCustom: trimmed,
  };
};

const buildFormState = (agent?: AgentRecord | null): AgentFormState => ({
  agentKey: agent?.agentKey ?? "",
  name: agent?.name ?? "",
  description: agent?.description ?? "",
  prompt: agent?.prompt ?? "",
  enabled: agent?.enabled ?? true,
  payloadTemplateText: formatJsonText(agent?.payloadTemplate),
  allowedFieldsText: (agent?.allowedFields ?? []).join("\n"),
  lockedFieldsText: (agent?.lockedFields ?? []).join("\n"),
  prefetchEndpoint: agent?.prefetchEndpoint ?? "",
  actionEndpoint: agent?.actionEndpoint ?? "",
  actionMethod: agent?.actionMethod ?? "POST",
  confirmLabel: agent?.confirmLabel ?? "Confirm",
  successMarkdown: agent?.successMarkdown ?? "✅ Agent action completed.",
  tags: agent?.tags ?? [],
  ...buildPageState(agent?.page),
});

const truncateText = (value?: string | null, limit: number = 96) => {
  const text = (value ?? "").replace(/\s+/g, " ").trim();
  if (!text) {
    return "--";
  }
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
};

const METHOD_OPTIONS = ["POST", "PUT", "PATCH", "DELETE"];

const useAutosizeTextArea = (
  ref: React.RefObject<HTMLTextAreaElement | null>,
  value: string
) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [ref, value]);
};

type PasteDiff = {
  toCreate: AgentRecord[];
  toUpdate: { existing: AgentRecord; incoming: AgentRecord }[];
  toDelete: AgentRecord[];
};

const Agents: React.FC = () => {
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<SelectedAgentId>(null);
  const [editingAgentId, setEditingAgentId] = useState<SelectedAgentId>(null);
  const [formState, setFormState] = useState<AgentFormState>(emptyAgent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pasteDiff, setPasteDiff] = useState<PasteDiff | null>(null);
  const [pasteProcessing, setPasteProcessing] = useState(false);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  const allowedFieldsRef = useRef<HTMLTextAreaElement | null>(null);
  const lockedFieldsRef = useRef<HTMLTextAreaElement | null>(null);
  const successRef = useRef<HTMLTextAreaElement | null>(null);
  useAutosizeTextArea(descriptionRef, formState.description);
  useAutosizeTextArea(promptRef, formState.prompt);
  useAutosizeTextArea(allowedFieldsRef, formState.allowedFieldsText);
  useAutosizeTextArea(lockedFieldsRef, formState.lockedFieldsText);
  useAutosizeTextArea(successRef, formState.successMarkdown);

  const selectedAgent = useMemo(() => {
    if (!selectedAgentId || selectedAgentId === "new") {
      return null;
    }
    return agents.find((agent) => agent.agentId === selectedAgentId) ?? null;
  }, [selectedAgentId, agents]);

  const filteredAgents = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return agents;
    }
    return agents.filter((agent) => {
      const haystack = `${agent.name} ${agent.agentKey} ${agent.description ?? ""} ${agent.prompt}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [agents, searchQuery]);

  const isEditing = editingAgentId !== null;

  useEffect(() => {
    const loadAgents = async () => {
      try {
        setLoading(true);
        const data = await fetchAgents();
        setAgents(data);
        if (data.length > 0) {
          const firstAgent = data[0];
          setSelectedAgentId(firstAgent.agentId);
          setFormState(buildFormState(firstAgent));
        }
      } catch (err) {
        console.error("OperatorFE: failed to load agents", err);
        setError("Unable to load agents. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    loadAgents();
  }, []);

  const refreshAgents = async (keepSelection?: string) => {
    const data = await fetchAgents();
    setAgents(data);
    if (data.length === 0) {
      setSelectedAgentId(null);
      setFormState(emptyAgent);
      return;
    }
    const nextSelection = keepSelection && data.some((agent) => agent.agentId === keepSelection)
      ? keepSelection
      : data[0].agentId;
    setSelectedAgentId(nextSelection);
    const active = data.find((agent) => agent.agentId === nextSelection);
    setFormState(buildFormState(active));
  };

  const handleSelectAgent = (agentId: string) => {
    if (isEditing && editingAgentId !== agentId) {
      return;
    }
    setSelectedAgentId(agentId);
    const agent = agents.find((item) => item.agentId === agentId);
    setFormState(buildFormState(agent));
    setEditingAgentId(null);
    setError(null);
  };

  const handleNewAgent = () => {
    setSelectedAgentId("new");
    setEditingAgentId("new");
    setFormState(emptyAgent);
    setError(null);
  };

  const handleStartEditing = () => {
    if (!selectedAgentId) {
      return;
    }
    setEditingAgentId(selectedAgentId);
    if (selectedAgentId === "new") {
      setFormState(emptyAgent);
    } else if (selectedAgent) {
      setFormState(buildFormState(selectedAgent));
    }
    setError(null);
  };

  const handleCancelEdit = () => {
    if (selectedAgentId === "new") {
      if (agents[0]) {
        setSelectedAgentId(agents[0].agentId);
        setFormState(buildFormState(agents[0]));
      } else {
        setSelectedAgentId(null);
        setFormState(emptyAgent);
      }
    } else if (selectedAgent) {
      setFormState(buildFormState(selectedAgent));
    }
    setEditingAgentId(null);
    setError(null);
  };

  const handleToggleEnabled = () => {
    setFormState((prev) => ({ ...prev, enabled: !prev.enabled }));
  };

  const handleTogglePageMode = () => {
    if (!isEditing) {
      return;
    }
    setFormState((prev) => {
      if (prev.pageMode === "prefixed") {
        const full = prev.pageSuffix
          ? `${DEFAULT_PAGE_PREFIX}${prev.pageSuffix.replace(/^\/+/g, "")}`
          : prev.pageCustom;
        return {
          ...prev,
          pageMode: "custom",
          pageCustom: full ?? "",
        };
      }
      const candidate = prev.pageCustom ?? "";
      const suffix = candidate.startsWith(DEFAULT_PAGE_PREFIX)
        ? candidate.slice(DEFAULT_PAGE_PREFIX.length)
        : candidate;
      return {
        ...prev,
        pageMode: "prefixed",
        pageSuffix: suffix.replace(/^\/+/g, ""),
        pageCustom: "",
      };
    });
  };

  const handleFormChange = (field: keyof AgentFormState, value: string | boolean) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      tags: Array.from(new Set([...(prev.tags ?? []), trimmed])),
    }));
    setTagInput("");
  };

  const handleRemoveTag = (tag: string) => {
    setFormState((prev) => ({
      ...prev,
      tags: (prev.tags ?? []).filter((item) => item !== tag),
    }));
  };

  const handleTagKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      handleAddTag();
    }
  };

  const parseJsonText = (text: string, fieldLabel: string) => {
    const trimmed = text.trim();
    if (!trimmed) {
      return {};
    }
    try {
      return JSON.parse(trimmed);
    } catch (err) {
      throw new Error(`${fieldLabel} must be valid JSON.`);
    }
  };

  const parseFieldList = (text: string) =>
    text
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);

  const handleSave = async () => {
    if (!formState.agentKey.trim()) {
      setError("Agent key is required.");
      return;
    }
    if (!formState.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!formState.prompt.trim()) {
      setError("Agent prompt is required.");
      return;
    }

    let payloadTemplate: any;
    try {
      payloadTemplate = parseJsonText(formState.payloadTemplateText, "Payload template");
    } catch (jsonError: any) {
      setError(jsonError.message);
      return;
    }

    const endpoint = formState.actionEndpoint.trim();
    const prefetchEndpoint = formState.prefetchEndpoint.trim();
    let pageValue: string | undefined;
    if (formState.pageMode === "prefixed") {
      const suffix = formState.pageSuffix.trim().replace(/^\/+/g, "");
      if (suffix) {
        pageValue = `${DEFAULT_PAGE_PREFIX}${suffix}`;
      }
    } else {
      const custom = formState.pageCustom.trim();
      if (custom) {
        pageValue = custom;
      }
    }
    const payload: AgentPayload = {
      agentId: selectedAgentId === "new" ? undefined : selectedAgentId ?? undefined,
      agentKey: formState.agentKey.trim(),
      name: formState.name.trim(),
      description: formState.description.trim(),
      prompt: formState.prompt.trim(),
      enabled: formState.enabled,
      payloadTemplate,
      tags: formState.tags,
      allowedFields: parseFieldList(formState.allowedFieldsText),
      lockedFields: parseFieldList(formState.lockedFieldsText),
      page: pageValue,
      prefetchEndpoint: prefetchEndpoint.length > 0 ? prefetchEndpoint : undefined,
      actionEndpoint: endpoint.length > 0 ? endpoint : undefined,
      actionMethod: formState.actionMethod,
      confirmLabel: formState.confirmLabel.trim() || "Confirm",
      successMarkdown: formState.successMarkdown.trim(),
    };

    setSaving(true);
    setError(null);
    try {
      const saved = await saveAgent(payload);
      await refreshAgents(saved.agentId);
      setEditingAgentId(null);
    } catch (err) {
      console.error("OperatorFE: failed to save agent", err);
      setError("Unable to save the agent. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const promptDelete = () => {
    if (!selectedAgentId || selectedAgentId === "new") {
      return;
    }
    setConfirmDeleteId(selectedAgentId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) {
      return;
    }
    setSaving(true);
    try {
      await deleteAgent(confirmDeleteId);
      setConfirmDeleteId(null);
      await refreshAgents();
      setEditingAgentId(null);
    } catch (err) {
      console.error("OperatorFE: failed to delete agent", err);
      setError("Unable to delete the agent. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) {
      return "--";
    }
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  const copyAllAgents = async () => {
    if (agents.length === 0) {
      return;
    }
    const clipboardPayload = {
      type: "agents-bulk",
      data: agents,
      ts: Date.now(),
    };
    try {
      const serialized = JSON.stringify(clipboardPayload);
      localStorage.setItem(SUPERADMIN_CLIPBOARD_KEY, serialized);
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${SUPERADMIN_CLIPBOARD_PREFIX}${serialized}`);
      }
    } catch (clipError) {
      console.error("OperatorFE: failed to copy agents", clipError);
    }
  };

  const readBulkAgentsClipboard = async (): Promise<AgentRecord[] | null> => {
    if (typeof window === "undefined") {
      return null;
    }
    if (navigator?.clipboard?.readText) {
      try {
        const text = await navigator.clipboard.readText();
        if (text?.startsWith(SUPERADMIN_CLIPBOARD_PREFIX)) {
          const payload = JSON.parse(text.slice(SUPERADMIN_CLIPBOARD_PREFIX.length));
          if (payload?.type === "agents-bulk" && Array.isArray(payload?.data)) {
            return payload.data as AgentRecord[];
          }
        }
      } catch (clipError) {
        console.error("OperatorFE: failed to read clipboard (agents-bulk)", clipError);
      }
    }
    try {
      const raw = localStorage.getItem(SUPERADMIN_CLIPBOARD_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.type === "agents-bulk" && Array.isArray(parsed?.data)) {
        return parsed.data as AgentRecord[];
      }
    } catch (clipError) {
      console.error("OperatorFE: failed to read agents-bulk clipboard", clipError);
    }
    return null;
  };

  const agentsAreEqual = (a: AgentRecord, b: AgentRecord): boolean => {
    return (
      a.name === b.name &&
      (a.description ?? "") === (b.description ?? "") &&
      a.prompt === b.prompt &&
      a.enabled === b.enabled &&
      JSON.stringify(a.payloadTemplate ?? {}) === JSON.stringify(b.payloadTemplate ?? {}) &&
      JSON.stringify(a.tags ?? []) === JSON.stringify(b.tags ?? []) &&
      JSON.stringify(a.allowedFields ?? []) === JSON.stringify(b.allowedFields ?? []) &&
      JSON.stringify(a.lockedFields ?? []) === JSON.stringify(b.lockedFields ?? []) &&
      (a.page ?? "") === (b.page ?? "") &&
      (a.prefetchEndpoint ?? "") === (b.prefetchEndpoint ?? "") &&
      (a.actionEndpoint ?? "") === (b.actionEndpoint ?? "") &&
      (a.actionMethod ?? "POST") === (b.actionMethod ?? "POST") &&
      (a.confirmLabel ?? "Confirm") === (b.confirmLabel ?? "Confirm") &&
      (a.successMarkdown ?? "") === (b.successMarkdown ?? "")
    );
  };

  const computePasteDiff = (incoming: AgentRecord[]): PasteDiff => {
    const existingByKey = new Map(agents.map((agent) => [agent.agentKey, agent]));
    const incomingByKey = new Map(incoming.map((agent) => [agent.agentKey, agent]));

    const toCreate: AgentRecord[] = [];
    const toUpdate: { existing: AgentRecord; incoming: AgentRecord }[] = [];
    const toDelete: AgentRecord[] = [];

    for (const agent of incoming) {
      const existing = existingByKey.get(agent.agentKey);
      if (existing) {
        if (!agentsAreEqual(existing, agent)) {
          toUpdate.push({ existing, incoming: agent });
        }
      } else {
        toCreate.push(agent);
      }
    }

    for (const agent of agents) {
      if (!incomingByKey.has(agent.agentKey)) {
        toDelete.push(agent);
      }
    }

    return { toCreate, toUpdate, toDelete };
  };

  const handlePasteAllClick = async () => {
    const incoming = await readBulkAgentsClipboard();
    if (!incoming || incoming.length === 0) {
      setError("Clipboard does not contain agent data.");
      return;
    }
    setError(null);
    const diff = computePasteDiff(incoming);
    setPasteDiff(diff);
  };

  const handleConfirmPaste = async () => {
    if (!pasteDiff) {
      return;
    }
    setPasteProcessing(true);
    setError(null);
    try {
      // Delete agents not in incoming
      for (const agent of pasteDiff.toDelete) {
        await deleteAgent(agent.agentId);
      }
      // Update existing agents
      for (const { existing, incoming } of pasteDiff.toUpdate) {
        const payload: AgentPayload = {
          agentId: existing.agentId,
          agentKey: incoming.agentKey,
          name: incoming.name,
          description: incoming.description ?? "",
          prompt: incoming.prompt,
          enabled: incoming.enabled,
          payloadTemplate: incoming.payloadTemplate,
          tags: incoming.tags ?? [],
          allowedFields: incoming.allowedFields ?? [],
          lockedFields: incoming.lockedFields ?? [],
          page: incoming.page ?? undefined,
          prefetchEndpoint: incoming.prefetchEndpoint ?? undefined,
          actionEndpoint: incoming.actionEndpoint ?? undefined,
          actionMethod: incoming.actionMethod ?? "POST",
          confirmLabel: incoming.confirmLabel ?? "Confirm",
          successMarkdown: incoming.successMarkdown ?? "",
        };
        await saveAgent(payload);
      }
      // Create new agents
      for (const agent of pasteDiff.toCreate) {
        const payload: AgentPayload = {
          agentKey: agent.agentKey,
          name: agent.name,
          description: agent.description ?? "",
          prompt: agent.prompt,
          enabled: agent.enabled,
          payloadTemplate: agent.payloadTemplate,
          tags: agent.tags ?? [],
          allowedFields: agent.allowedFields ?? [],
          lockedFields: agent.lockedFields ?? [],
          page: agent.page ?? undefined,
          prefetchEndpoint: agent.prefetchEndpoint ?? undefined,
          actionEndpoint: agent.actionEndpoint ?? undefined,
          actionMethod: agent.actionMethod ?? "POST",
          confirmLabel: agent.confirmLabel ?? "Confirm",
          successMarkdown: agent.successMarkdown ?? "",
        };
        await saveAgent(payload);
      }
      await refreshAgents();
      setPasteDiff(null);
    } catch (err) {
      console.error("OperatorFE: failed to paste agents", err);
      setError("Failed to apply paste. Please try again.");
    } finally {
      setPasteProcessing(false);
    }
  };

  const renderDetail = () => {
    if (loading) {
      return <div className="docs-empty">Loading agents...</div>;
    }
    if (!selectedAgentId) {
      return <div className="docs-empty">Select or create an agent.</div>;
    }
    return (
      <div className="docs-editor">
        <div className="docs-editor-actions">
          <div className="docs-editor-actions-left">
            {isEditing ? (
              <>
                <button
                  className="docs-button docs-button--primary"
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save
                </button>
                <button className="docs-button docs-button--secondary" type="button" onClick={handleCancelEdit}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  className="docs-button docs-button--secondary"
                  type="button"
                  onClick={handleStartEditing}
                  disabled={!selectedAgentId}
                >
                  Edit
                </button>
                <button
                  className="docs-button docs-button--danger"
                  type="button"
                  onClick={promptDelete}
                  disabled={!selectedAgentId || selectedAgentId === "new"}
                >
                  Delete
                </button>
              </>
            )}
          </div>
          <div className="docs-editor-actions-right">
            <div className="docs-toggle">
              <Slider checked={formState.enabled} onChange={handleToggleEnabled} disabled={!isEditing} />
              <span>{formState.enabled ? "Live" : "Paused"}</span>
            </div>
          </div>
        </div>
        {error && <div className="docs-error">{error}</div>}
        <div className="docs-editor-body">
          <div className="docs-field">
            <label htmlFor="agent-key">Agent Key</label>
            <input
              id="agent-key"
              type="text"
              className="docs-input--mono"
              placeholder="unique-agent-key"
              value={formState.agentKey}
              onChange={(event) => handleFormChange("agentKey", event.target.value)}
              disabled={selectedAgentId !== "new" && !isEditing}
            />
          </div>
          <div className="docs-field">
            <label htmlFor="agent-name">Name</label>
            <input
              id="agent-name"
              type="text"
              placeholder="Agent Name"
              value={formState.name}
              onChange={(event) => handleFormChange("name", event.target.value)}
              disabled={!isEditing}
            />
          </div>
          <div className="docs-field">
            <label htmlFor="agent-description">Description</label>
            <textarea
              id="agent-description"
              ref={descriptionRef}
              rows={3}
              placeholder="Brief description of the agent..."
              value={formState.description}
              onChange={(event) => handleFormChange("description", event.target.value)}
              disabled={!isEditing}
            />
          </div>
          <div className="docs-field">
            <label htmlFor="agent-prompt">Agent Prompt</label>
            <textarea
              id="agent-prompt"
              ref={promptRef}
              rows={8}
              value={formState.prompt}
              onChange={(event) => handleFormChange("prompt", event.target.value)}
              disabled={!isEditing}
              placeholder="Describe the agent's goals and required output format..."
            />
          </div>
          <div className="docs-field">
            <label>Tags</label>
            <div className="docs-tags">
              {isEditing && (
                <div className="docs-tag-input">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(event) => setTagInput(event.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Add tag and press Enter"
                  />
                  <button
                    type="button"
                    className="docs-button docs-button--primary"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                  >
                    Add
                  </button>
                </div>
              )}
              <div className="docs-tag-list">
                {formState.tags.length === 0 && !isEditing && <span>No tags added</span>}
                {formState.tags.map((tag) => (
                  <span key={tag} className="docs-tag">
                    {tag}
                    {isEditing && (
                      <button type="button" onClick={() => handleRemoveTag(tag)} aria-label={`Remove ${tag}`}>
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="docs-field">
            <label htmlFor="agent-payload-template">Payload Template JSON</label>
            <span className="docs-field-hint">
              JSON structure the agent fills before the confirmation button fires.
            </span>
            <JsonEditor
              id="agent-payload-template"
              value={formState.payloadTemplateText}
              onChange={(value) => handleFormChange("payloadTemplateText", value)}
              disabled={!isEditing}
              rows={8}
              placeholder="{ }"
            />
          </div>
          <div className="docs-field">
            <label htmlFor="agent-allowed-fields">Allowed Fields</label>
            <span className="docs-field-hint">
              Only these keys can be modified by the agent. Separate with commas or new lines.
            </span>
            <textarea
              id="agent-allowed-fields"
              ref={allowedFieldsRef}
              rows={3}
              className="docs-input--mono"
              value={formState.allowedFieldsText}
              onChange={(event) => handleFormChange("allowedFieldsText", event.target.value)}
              disabled={!isEditing}
              placeholder={"field1\nfield2"}
            />
          </div>
          <div className="docs-field">
            <label htmlFor="agent-locked-fields">Locked Fields</label>
            <span className="docs-field-hint">
              Keys listed here always match the prefetched snapshot.
            </span>
            <textarea
              id="agent-locked-fields"
              ref={lockedFieldsRef}
              rows={3}
              className="docs-input--mono"
              value={formState.lockedFieldsText}
              onChange={(event) => handleFormChange("lockedFieldsText", event.target.value)}
              disabled={!isEditing}
              placeholder={"field1\nfield2"}
            />
          </div>
          <div className="docs-field">
            <label htmlFor="agent-view-page">Agent View Page</label>
            <span className="docs-field-hint">
              Determines where the operator is sent after approving the action.
            </span>
            <div className="docs-path">
              {formState.pageMode === "prefixed" ? (
                <>
                  <span className="docs-path-prefix">{DEFAULT_PAGE_PREFIX}</span>
                  <input
                    id="agent-view-page"
                    type="text"
                    className="docs-input--mono"
                    value={formState.pageSuffix}
                    onChange={(event) => handleFormChange("pageSuffix", event.target.value)}
                    disabled={!isEditing}
                    placeholder="settings/pricing"
                  />
                </>
              ) : (
                <input
                  id="agent-view-page-custom"
                  type="text"
                  className="docs-input--mono"
                  value={formState.pageCustom}
                  onChange={(event) => handleFormChange("pageCustom", event.target.value)}
                  disabled={!isEditing}
                  placeholder="/lot/{lotId}/settings/pricing"
                />
              )}
              <button
                type="button"
                className="docs-button docs-button--ghost docs-inline-button"
                onClick={handleTogglePageMode}
                disabled={!isEditing}
              >
                {formState.pageMode === "prefixed" ? "Custom URL" : "Lot path"}
              </button>
            </div>
          </div>
          <div className="docs-field">
            <label htmlFor="agent-prefetch">Prefetch Endpoint (optional)</label>
            <span className="docs-field-hint">
              GET endpoint triggered before the model responds. Supports placeholders like <code>{"{lotId}"}</code>.
            </span>
            <div className="docs-endpoint-row">
              <span className="docs-endpoint-label">GET</span>
              <input
                id="agent-prefetch"
                type="text"
                value={formState.prefetchEndpoint}
                onChange={(event) => handleFormChange("prefetchEndpoint", event.target.value)}
                disabled={!isEditing}
                placeholder="ex: /lotpricing/get-latest-pricing-by-lot-id/{lotId}"
              />
            </div>
          </div>
          <div className="docs-field">
            <label htmlFor="agent-endpoint">Action Endpoint</label>
            <div className="docs-endpoint-row">
              <div className="docs-endpoint-method">
                <Menu
                  trigger={
                    <button
                      type="button"
                      className="docs-endpoint-method-trigger"
                      disabled={!isEditing}
                    >
                      {formState.actionMethod}
                    </button>
                  }
                  items={METHOD_OPTIONS.map((method) => ({
                    key: method,
                    label: method,
                    selected: formState.actionMethod === method,
                    onSelect: () => handleFormChange("actionMethod", method),
                  }))}
                  matchTriggerWidth={false}
                />
              </div>
              <input
                id="agent-endpoint"
                type="text"
                value={formState.actionEndpoint}
                onChange={(event) => handleFormChange("actionEndpoint", event.target.value)}
                disabled={!isEditing}
                placeholder="ex: /lotpricing/update-pricing-by-lot-id/{lotId}/{pricingId}"
              />
            </div>
          </div>
          <div className="docs-field">
            <label htmlFor="agent-confirm-label">Confirmation Button Label</label>
            <input
              id="agent-confirm-label"
              type="text"
              value={formState.confirmLabel}
              onChange={(event) => handleFormChange("confirmLabel", event.target.value)}
              disabled={!isEditing}
              placeholder="Confirm"
            />
          </div>
          <div className="docs-field">
            <label htmlFor="agent-success">Success Message</label>
            <textarea
              id="agent-success"
              ref={successRef}
              rows={3}
              value={formState.successMarkdown}
              onChange={(event) => handleFormChange("successMarkdown", event.target.value)}
              disabled={!isEditing}
              placeholder="Agent action completed."
            />
          </div>
          {selectedAgent && (
            <div className="docs-info">
              <span>Created: {formatDate(selectedAgent.createdOn)}</span>
              <span>Updated: {formatDate(selectedAgent.modifiedOn)}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="docs-page">
      <div className="docs-shell">
        <div className="docs-header">
          <div>
            <h1>AI Agents</h1>
            <p>Deploy lot-aware assistants that operate safely with defined payloads and endpoints.</p>
          </div>
          <div className="docs-actions">
            <button
              type="button"
              className="docs-button docs-button--primary"
              onClick={handleNewAgent}
              disabled={isEditing && editingAgentId !== "new"}
            >
              New Agent
            </button>
          </div>
        </div>

        <div className="docs-grid">
          <aside className="docs-pane">
            <div className="docs-pane-header">
              <h2>Agents</h2>
              <div className="docs-bulk-actions">
                <button
                  type="button"
                  className="docs-button docs-button--secondary"
                  onClick={copyAllAgents}
                  disabled={agents.length === 0 || isEditing}
                >
                  Copy All
                </button>
                <button
                  type="button"
                  className="docs-button docs-button--secondary"
                  onClick={handlePasteAllClick}
                  disabled={isEditing}
                >
                  Paste All
                </button>
              </div>
              <input
                type="text"
                className="docs-search-input"
                placeholder="Search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
            <div className="docs-pane-body">
              {filteredAgents.length === 0 && !loading ? (
                <div className="docs-empty">No agents match your search.</div>
              ) : (
                <ul className="docs-list">
                  {filteredAgents.map((agent) => (
                    <li
                      key={agent.agentId}
                      className={`docs-list-item ${selectedAgentId === agent.agentId ? "active" : ""}`}
                      onClick={() => handleSelectAgent(agent.agentId)}
                    >
                      <div className="docs-list-title">{agent.name || agent.agentKey}</div>
                      <div className="docs-list-meta">
                        <span>{agent.agentKey}</span>
                        <p className="docs-list-description">{truncateText(agent.description || agent.prompt)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
          <section className="docs-pane">{renderDetail()}</section>
        </div>
      </div>

      <Modal
        isOpen={Boolean(confirmDeleteId)}
        title="Delete agent?"
        description="This action cannot be undone. The agent will no longer be available in chat."
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmDelete}
        disableConfirm={saving}
      />

      <Modal
        isOpen={Boolean(pasteDiff)}
        title="Confirm Paste All"
        confirmText="Confirm"
        cancelText="Cancel"
        onCancel={() => setPasteDiff(null)}
        onConfirm={handleConfirmPaste}
        disableConfirm={pasteProcessing}
        customContent={
          pasteDiff && (
            <div className="docs-paste-diff">
              {pasteDiff.toCreate.length > 0 && (
                <div className="docs-paste-section">
                  <strong>Creating ({pasteDiff.toCreate.length}):</strong>
                  <ul>
                    {pasteDiff.toCreate.map((agent) => (
                      <li key={agent.agentKey}>{agent.name || agent.agentKey}</li>
                    ))}
                  </ul>
                </div>
              )}
              {pasteDiff.toUpdate.length > 0 && (
                <div className="docs-paste-section">
                  <strong>Updating ({pasteDiff.toUpdate.length}):</strong>
                  <ul>
                    {pasteDiff.toUpdate.map(({ existing }) => (
                      <li key={existing.agentKey}>{existing.name || existing.agentKey}</li>
                    ))}
                  </ul>
                </div>
              )}
              {pasteDiff.toDelete.length > 0 && (
                <div className="docs-paste-section">
                  <strong>Deleting ({pasteDiff.toDelete.length}):</strong>
                  <ul>
                    {pasteDiff.toDelete.map((agent) => (
                      <li key={agent.agentKey}>{agent.name || agent.agentKey}</li>
                    ))}
                  </ul>
                </div>
              )}
              {pasteDiff.toCreate.length === 0 && pasteDiff.toUpdate.length === 0 && pasteDiff.toDelete.length === 0 && (
                <p>No changes to apply.</p>
              )}
            </div>
          )
        }
      />
    </div>
  );
};

export default Agents;
